import { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  Chip,
  IconButton,
  Dialog,
  DialogHeader,
  DialogBody,
  Tooltip,
} from "@material-tailwind/react";
import { ChevronDownIcon, XMarkIcon, Cog6ToothIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "@material-tailwind/react";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import { and, collection, doc, query, where, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import db from "../firebase";
import { periodToDate } from "../utils";
import { extractUnknownRegs } from "../fb-functions";

// "On market" = seen by any scrape within this window.
const PERIOD = "7d";

// The Cirrus network: aircraft in the cirrusaircraft.com pre-owned feed.
// Membership comes ONLY from cirrus-source docs, where dealerName is
// canonical ("Cirrus Next" / "Aerista" / "Lone Mountain Aircraft" from the
// listing URL host) — free-text seller names on other sites false-positive
// (e.g. "Cirrus Aircraft CZ s.r.o."). Network-broker listings that are NOT
// in the feed (verified live on broker sites, not sold) are tracked as the
// separate "off-feed" group below and can be toggled into the figures.
export const PARTNERS = [
  { key: "lone_mountain", label: "Lone Mountain", short: "LMA", color: "#d4a017", match: /lone\s*mountain/i },
  { key: "aerista", label: "Aerista", short: "Aerista", color: "#2196f3", match: /aerista/i },
  { key: "cirrus_direct", label: "Cirrus Direct", short: "Direct", color: "#e53935", match: /^cirrus\s*(next|aircraft)\s*$/i },
];

export const OFF_FEED = {
  key: "off_feed",
  label: "Off-feed broker listings",
  short: "Off-feed",
  sub: "live with network brokers, but not in the cirrus feed",
  color: "#7c3aed",
};

const SOURCE_LABELS = {
  cirrus: "Cirrus",
  controller: "Controller",
  tradeaplane: "Trade-A-Plane",
  planecheck: "Planecheck",
  globalair: "Globalair",
  aircraftpost: "AircraftPost",
};

// ---- geography classification --------------------------------------------
// Three independent bases, so their impact can be compared in the UI:
//   country  — the doc's country field (per-source spellings normalized)
//   reg      — ICAO registration prefix (the aircraft's registry)
//   dealer   — country parsed from the free-text dealerLocation string
// They usually agree; they diverge for e.g. an N-reg aircraft for sale in
// Europe, or network brokers whose dealer address is a US HQ.

const COUNTRY_ALIASES = {
  "usa": "United States", "us": "United States", "u.s.a.": "United States",
  "united states": "United States", "united states of america": "United States",
  "uk": "United Kingdom", "united kingdom": "United Kingdom", "great britain": "United Kingdom",
  "england": "United Kingdom", "scotland": "United Kingdom", "wales": "United Kingdom",
  "northern ireland": "United Kingdom",
  "holland": "Netherlands", "the netherlands": "Netherlands",
  "czechia": "Czech Republic", "czech rep.": "Czech Republic", "czech rep": "Czech Republic",
  "deutschland": "Germany", "turkiye": "Turkey", "türkiye": "Turkey",
  "korea": "South Korea", "uae": "United Arab Emirates",
};

// The country field is usually a clean country name, but some sources stuff
// full location strings ("Padua, Ven., Italy", "US Minnesota") or sentinels
// ("UNKNOWN_LOCATION") into it — fall back to the same parsing used for
// dealerLocation before giving up.
function canonicalCountry(raw) {
  const v = (raw || "").trim();
  if (!v || /unknown/i.test(v)) return "Unknown";
  const alias = COUNTRY_ALIASES[v.toLowerCase()];
  if (alias) return alias;
  if (v === "United States" || EUROPEAN_COUNTRIES.has(v) || KNOWN_COUNTRIES.includes(v)) return v;
  const parsed = countryFromDealerLocation(v);
  return parsed !== "Unknown" ? parsed : v;
}

const EUROPEAN_COUNTRIES = new Set([
  "United Kingdom", "Ireland", "France", "Germany", "Switzerland", "Austria",
  "Italy", "Spain", "Portugal", "Netherlands", "Belgium", "Luxembourg",
  "Denmark", "Norway", "Sweden", "Finland", "Iceland", "Poland",
  "Czech Republic", "Slovakia", "Hungary", "Slovenia", "Croatia", "Serbia",
  "Romania", "Bulgaria", "Greece", "Malta", "Cyprus", "Estonia", "Latvia",
  "Lithuania", "Monaco", "San Marino", "Isle of Man", "Guernsey", "Jersey",
  "Gibraltar", "Turkey",
]);

// Longest-prefix-first match against the normalized (dash-stripped) reg.
const REG_PREFIXES = [
  ["VH", "Australia"], ["VP", "Bermuda/Cayman"], ["VQ", "Bermuda/Cayman"],
  ["CF", "Canada"], ["CG", "Canada"], ["CI", "Canada"],
  ["HB", "Switzerland"], ["OE", "Austria"], ["OK", "Czech Republic"],
  ["OM", "Slovakia"], ["OO", "Belgium"], ["OY", "Denmark"], ["OH", "Finland"],
  ["LN", "Norway"], ["SE", "Sweden"], ["SP", "Poland"], ["EI", "Ireland"],
  ["EC", "Spain"], ["CS", "Portugal"], ["PH", "Netherlands"], ["LX", "Luxembourg"],
  ["HA", "Hungary"], ["S5", "Slovenia"], ["9A", "Croatia"], ["YR", "Romania"],
  ["LZ", "Bulgaria"], ["SX", "Greece"], ["9H", "Malta"], ["5B", "Cyprus"],
  ["ES", "Estonia"], ["YL", "Latvia"], ["LY", "Lithuania"], ["T7", "San Marino"],
  ["TC", "Turkey"], ["4X", "Israel"], ["A6", "United Arab Emirates"],
  ["ZS", "South Africa"], ["ZK", "New Zealand"], ["JA", "Japan"], ["HL", "South Korea"],
  ["9V", "Singapore"], ["9M", "Malaysia"], ["HS", "Thailand"], ["PK", "Indonesia"],
  ["RP", "Philippines"], ["VT", "India"], ["AP", "Pakistan"], ["HK", "Colombia"],
  ["LV", "Argentina"], ["CC", "Chile"], ["PR", "Brazil"], ["PP", "Brazil"],
  ["PT", "Brazil"], ["PU", "Brazil"], ["XA", "Mexico"], ["XB", "Mexico"], ["XC", "Mexico"],
  ["TF", "Iceland"], ["N", "United States"], ["C", "Canada"], ["D", "Germany"],
  ["G", "United Kingdom"], ["F", "France"], ["I", "Italy"], ["B", "China"],
  ["M", "Isle of Man"], ["2", "Guernsey"],
];

function countryFromReg(reg) {
  const r = (reg || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (!r) return "Unknown";
  const two = REG_PREFIXES.find(([p]) => p.length === 2 && r.startsWith(p));
  if (two) return two[1];
  const one = REG_PREFIXES.find(([p]) => p.length === 1 && r.startsWith(p));
  return one ? one[1] : "Unknown";
}

const US_STATE_TOKENS = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
]);
const US_STATE_NAMES = [
  "alabama","alaska","arizona","arkansas","california","colorado","connecticut",
  "delaware","florida","georgia","hawaii","idaho","illinois","indiana","iowa",
  "kansas","kentucky","louisiana","maine","maryland","massachusetts","michigan",
  "minnesota","mississippi","missouri","montana","nebraska","nevada",
  "new hampshire","new jersey","new mexico","new york","north carolina",
  "north dakota","ohio","oklahoma","oregon","pennsylvania","rhode island",
  "south carolina","south dakota","tennessee","texas","utah","vermont",
  "virginia","washington","west virginia","wisconsin","wyoming",
];
// Non-US, non-European countries grouped into named regions. Anything that
// maps to a country not listed here (rare) falls into "Other".
const REGION_COUNTRIES = {
  canada: ["Canada"],
  asiapac: [
    "China", "Hong Kong", "Taiwan", "Japan", "South Korea", "Singapore",
    "Malaysia", "Thailand", "Indonesia", "Philippines", "Vietnam", "India",
    "Pakistan", "Australia", "New Zealand",
  ],
  mideast: [
    "Israel", "United Arab Emirates", "Oman", "Saudi Arabia", "Qatar",
    "Bahrain", "Kuwait", "Jordan", "Lebanon",
  ],
  latam: [
    "Mexico", "Guatemala", "Costa Rica", "Panama", "Colombia", "Venezuela",
    "Ecuador", "Peru", "Chile", "Argentina", "Uruguay", "Brazil", "Aruba",
    "Bermuda/Cayman", "Bahamas", "Puerto Rico",
  ],
  africa: ["South Africa", "Kenya", "Nigeria", "Morocco", "Egypt", "Botswana", "Namibia"],
};

const COUNTRY_REGION = new Map();
EUROPEAN_COUNTRIES.forEach((c) => COUNTRY_REGION.set(c, "europe"));
Object.entries(REGION_COUNTRIES).forEach(([r, list]) => list.forEach((c) => COUNTRY_REGION.set(c, r)));

const KNOWN_COUNTRIES = [...COUNTRY_REGION.keys()];

function countryFromDealerLocation(loc) {
  const raw = (loc || "").trim();
  if (!raw) return "Unknown";
  const lower = raw.toLowerCase();
  // US signals first ("Georgia" etc. are read as states, which fits this dataset)
  if (/\busa\b|\bu\.s\.a\b|united states/i.test(raw)) return "United States";
  if (US_STATE_NAMES.some((s) => lower.includes(s))) return "United States";
  const tokens = raw.toUpperCase().split(/[^A-Z]+/);
  if (tokens.some((t) => US_STATE_TOKENS.has(t))) return "United States";
  for (const [alias, country] of Object.entries(COUNTRY_ALIASES)) {
    if (lower.includes(alias) && alias.length > 2) return country;
  }
  const named = KNOWN_COUNTRIES.find((c) => lower.includes(c.toLowerCase()));
  return named || "Unknown";
}

function regionOf(country) {
  if (country === "United States") return "us";
  if (country === "Unknown") return "unknown";
  return COUNTRY_REGION.get(country) || "other";
}

export const GEO_BASES = [
  { key: "country", label: "Country field" },
  { key: "reg", label: "Registration prefix" },
  { key: "dealer", label: "Dealer location" },
];
export const REGIONS = [
  { key: "all", label: "All regions" },
  { key: "us", label: "United States" },
  { key: "canada", label: "Canada" },
  { key: "europe", label: "Europe" },
  { key: "mideast", label: "Middle East" },
  { key: "asiapac", label: "Asia-Pacific" },
  { key: "latam", label: "Latin America" },
  { key: "africa", label: "Africa" },
  { key: "other", label: "Other" },
  { key: "unknown", label: "Unknown" },
];

// One aircraft can be listed on several sites, so aggregation docs are
// deduped by registration. Docs with no usable reg fall back to their
// uniqueListingId (counted as distinct aircraft). The sources use different
// missing-reg sentinels (cirrus: UNKNOWN_REG, controller: UNKNOWN, others:
// NO_REG / "Not Listed" / "EASA or FAA"...) — all must be treated as no-reg,
// otherwise every reg-less listing merges into one fake aircraft.
const PLACEHOLDER_REGS = new Set([
  "UNKNOWN", "UNKNOWNREG", "NOREG", "NOTLISTED", "NONE", "TBD", "NA",
  "EASAORFAA", "FAAOREASA", "EASA", "FAA", "CALL",
]);

function regKey(data) {
  const reg = (data.reg || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (reg && !PLACEHOLDER_REGS.has(reg)) return reg;
  // fall back to a registration read off the listing photos by the vision model
  const ai = (data.aiReg?.reg || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (ai) return ai;
  return `id:${data.uniqueListingId}`;
}

// The registration to display, and where it came from.
function displayReg(data) {
  const reg = (data.reg || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (reg && !PLACEHOLDER_REGS.has(reg)) return { reg: data.reg, source: "scrape" };
  if (data.aiReg?.reg) return { reg: data.aiReg.reg, source: "ai", confidence: data.aiReg.confidence };
  return { reg: "", source: "none" };
}

function partnerFor(dealerNames) {
  for (const p of PARTNERS) {
    if (dealerNames.some((d) => p.match.test(d))) return p.key;
  }
  return null;
}

// A cirrus-feed doc always classifies (its dealerName is one of the three
// canonical labels, or the "Cirrus Aircraft" fallback → Cirrus direct).
function partnerForCirrusFeed(dealerNames) {
  return partnerFor(dealerNames) || "cirrus_direct";
}

const pct = (n, d) => (d > 0 ? `${((100 * n) / d).toFixed(1)}%` : "n/a");

function fmtPrice(a) {
  const usd = Number(a.priceUsd);
  if (usd > 0) return `$${Math.round(usd).toLocaleString()}`;
  const raw = Number(a.price);
  if (!(raw > 0)) return "n/a";
  const cur = a.currency && a.currency !== "USD" ? `${a.currency} ` : "$";
  return `${cur}${Math.round(raw).toLocaleString()}`;
}

function fmtSeen(ts) {
  if (!ts) return "n/a";
  const days = Math.floor((Date.now() - ts) / 86400000);
  return days <= 0 ? "today" : `${days}d ago`;
}

// ---- gauge geometry (240° arc) -------------------------------------------
const GAUGE = { cx: 150, cy: 128, r: 106, start: -120, sweep: 240 };

function polar(cx, cy, r, deg) {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arcPath(startDeg, endDeg, r = GAUGE.r) {
  const [sx, sy] = polar(GAUGE.cx, GAUGE.cy, r, startDeg);
  const [ex, ey] = polar(GAUGE.cx, GAUGE.cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
}

export const NON_NETWORK_COLOR = "#90a4ae";

// Strict shared column grid for the channel/seller rows in BOTH panels:
// toggle · dot · name (fixed width, truncating) · bar · % market · % fleet ·
// chevron. Keeping one template means every bar starts on the same baseline
// regardless of how long the name is.
const CHANNEL_GRID = "grid grid-cols-[32px_12px_minmax(150px,220px)_1fr_64px_64px_20px] items-center gap-x-3";

function ChannelGridHeader({ nameLabel }) {
  return (
    <div className={`${CHANNEL_GRID} px-2 pb-1`}>
      <span />
      <span />
      <Typography variant="small" className="text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
        {nameLabel}
      </Typography>
      <Typography variant="small" className="text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
        Share
      </Typography>
      <Typography variant="small" className="text-right text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
        % market
      </Typography>
      <Typography variant="small" className="text-right text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
        % fleet
      </Typography>
      <span />
    </div>
  );
}

// Gauge segments for the enabled groups, laid tip-to-tail along the arc,
// with the remaining (non-network) share of the market as an explicit final
// segment. Shared by the hero gauge and the per-region mini gauges so they
// always read identically.
function buildSegments(groupDefs, counts, total, enabled) {
  if (!total) return [];
  const segs = [];
  let cursor = GAUGE.start;
  groupDefs.forEach((g) => {
    if (!enabled[g.key]) return;
    const frac = (counts[g.key] || 0) / total;
    if (frac <= 0) return;
    const sweep = frac * GAUGE.sweep;
    segs.push({ key: g.key, color: g.color, d: arcPath(cursor + 0.8, cursor + sweep - 0.8) });
    cursor += sweep;
  });
  const end = GAUGE.start + GAUGE.sweep;
  if (cursor < end - 1.6) {
    segs.push({ key: "non_network", color: NON_NETWORK_COLOR, d: arcPath(cursor + 0.8, end - 0.8) });
  }
  return segs;
}

function StatTile({ label, value, foot }) {
  return (
    <div className="flex flex-col justify-center bg-white p-5">
      <Typography variant="small" className="uppercase text-[11px] tracking-wide text-blue-gray-500">
        {label}
      </Typography>
      <Typography variant="h4" color="blue-gray" className="mt-1">
        {value}
      </Typography>
      <Typography variant="small" color="blue-gray" className="mt-1">
        {foot}
      </Typography>
    </div>
  );
}

const SORTS = {
  year: (a, b) => (b.year || 0) - (a.year || 0),
  price: (a, b) => (Number(b.priceUsd || b.price) || 0) - (Number(a.priceUsd || a.price) || 0),
  seen: (a, b) => (b.lastSeen || 0) - (a.lastSeen || 0),
};

function DrillDown({ group, aircraft, onClose, fleetSize, marketTotal, geoBasis }) {
  const [sort, setSort] = useState({ col: "seen", dir: 1 });

  const rows = useMemo(() => {
    const sorted = [...aircraft].sort(SORTS[sort.col]);
    if (sort.dir === -1) sorted.reverse();
    return sorted;
  }, [aircraft, sort]);

  const sortable = { year: "Year", price: "Price", seen: "Last seen" };
  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: -s.dir } : { col, dir: 1 }));

  return (
    <Card className="mt-4">
      <CardHeader floated={false} shadow={false} color="transparent" className="m-0 flex items-start justify-between p-6 pb-2">
        <div className="flex items-center gap-3">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: group.color }} />
          <div>
            <Typography variant="h6" color="blue-gray">
              {group.label} ({aircraft.length} aircraft)
            </Typography>
            <Typography variant="small" color="gray">
              {pct(aircraft.length, marketTotal)} of market
              {fleetSize ? ` · ${pct(aircraft.length, fleetSize)} of fleet` : ""} · click a row to
              open its listing details page (new tab), or a source chip for that site's live
              listing. External links reflect each site's last scrape, so an aircraft that just
              sold may 404.
            </Typography>
          </div>
        </div>
        <IconButton variant="text" color="blue-gray" size="sm" onClick={onClose}>
          <XMarkIcon className="h-4 w-4" />
        </IconButton>
      </CardHeader>
      <CardBody className="overflow-x-auto p-6 pt-2">
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full min-w-[720px] table-auto text-left">
            <thead>
              <tr>
                {[
                  ["reg", "Reg"],
                  ["year", "Year"],
                  ["model", "Model"],
                  ["price", "Price"],
                  ["dealer", "Dealer"],
                  ["geo", "Country"],
                  ["sources", "Sources"],
                  ["seen", "Last seen"],
                ].map(([col, label]) => (
                  <th
                    key={col}
                    className={`sticky top-0 z-10 border-b border-blue-gray-100 bg-white py-2 pr-4 ${
                      sortable[col] ? "cursor-pointer select-none" : ""
                    }`}
                    onClick={sortable[col] ? () => toggleSort(col) : undefined}
                  >
                    <Typography variant="small" className="text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
                      {label}
                      {sort.col === col ? (sort.dir === 1 ? " ↓" : " ↑") : ""}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr
                  key={a.key}
                  className={`border-b border-blue-gray-50 hover:bg-blue-gray-50/50 ${
                    a.listingId ? "cursor-pointer" : ""
                  }`}
                  onClick={() =>
                    a.listingId && window.open(`/dashboard/listing-details/${a.listingId}`, "_blank", "noopener")
                  }
                >
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1">
                      <Typography variant="small" color="blue-gray" className="font-mono font-medium">
                        {a.reg || "no reg"}
                      </Typography>
                      {a.regSource === "ai" && (
                        <span
                          className="rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-900"
                          title={`registration read from listing photos by AI (confidence ${Math.round((a.regConfidence || 0) * 100)}%)`}
                        >
                          AI
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <Typography variant="small" color="blue-gray">{a.year || "n/a"}</Typography>
                  </td>
                  <td className="py-2 pr-4">
                    <Typography variant="small" color="blue-gray">{a.model || "n/a"}</Typography>
                  </td>
                  <td className="py-2 pr-4">
                    <Typography variant="small" color="blue-gray">{fmtPrice(a)}</Typography>
                  </td>
                  <td className="max-w-[200px] py-2 pr-4">
                    <Typography variant="small" color="gray" className="truncate" title={a.dealer}>
                      {a.dealer || "n/a"}
                    </Typography>
                  </td>
                  <td className="py-2 pr-4">
                    <Typography
                      variant="small"
                      color="gray"
                      title={`country field: ${a.geo.country} · registration: ${a.geo.reg} · dealer location: ${a.geo.dealer}`}
                    >
                      {a.geo[geoBasis] || "Unknown"}
                    </Typography>
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {a.sources.map((s) => {
                        const stale = s.lastSeen && Date.now() - s.lastSeen > 48 * 3600 * 1000;
                        return (
                          <span key={s.source} title={`last seen on ${SOURCE_LABELS[s.source] || s.source} ${fmtSeen(s.lastSeen)}${stale ? ", may be delisted" : ""}`}>
                            <Chip
                              size="sm"
                              variant="ghost"
                              color={s.source === "cirrus" ? "red" : "blue-gray"}
                              value={SOURCE_LABELS[s.source] || s.source}
                              className={`${s.url ? "cursor-pointer" : ""} ${stale ? "opacity-40" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (s.url) window.open(s.url, "_blank", "noopener");
                              }}
                            />
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <Typography variant="small" color="gray">{fmtSeen(a.lastSeen)}</Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

const REGION_COLORS = {
  us: "#1e88e5",
  canada: "#d81b60",
  europe: "#43a047",
  mideast: "#f9a825",
  asiapac: "#8e24aa",
  latam: "#00897b",
  africa: "#fb8c00",
  other: "#90a4ae",
  unknown: "#cfd8dc",
};

const REGION_CHIP_COLORS = {
  us: "blue",
  canada: "red",
  europe: "green",
  mideast: "amber",
  asiapac: "purple",
  latam: "teal",
  africa: "orange",
  other: "blue-gray",
  unknown: "blue-gray",
};

function regPrefixOf(reg) {
  const r = (reg || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (!r) return "";
  const two = REG_PREFIXES.find(([p]) => p.length === 2 && r.startsWith(p));
  if (two) return two[0];
  const one = REG_PREFIXES.find(([p]) => p.length === 1 && r.startsWith(p));
  return one ? one[0] : "";
}

const COUNTRY_OPTIONS = [
  "United States",
  ...[...new Set(KNOWN_COUNTRIES)].sort(),
  "Unknown",
];

function MappingTable({ title, sub, rows, rawHeader, editable, overrides }) {
  const setOverride = async (raw, country) => {
    const ref = doc(db, "geo_mapping_overrides", encodeURIComponent(raw));
    if (country) {
      await setDoc(ref, { raw, country, updatedAt: serverTimestamp() });
    } else {
      await deleteDoc(ref);
    }
  };

  return (
    <Card>
      <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
        <Typography variant="h6" color="blue-gray">{title}</Typography>
        <Typography variant="small" color="gray">{sub}</Typography>
      </CardHeader>
      <CardBody className="p-6 pt-3">
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full table-auto text-left">
            <thead>
              <tr>
                {[rawHeader, "Mapped country", "Region", "Aircraft", ...(editable ? ["Override"] : [])].map((h) => (
                  <th key={h} className="sticky top-0 z-10 border-b border-blue-gray-100 bg-white py-2 pr-4">
                    <Typography variant="small" className="text-[11px] font-medium uppercase tracking-wide text-blue-gray-400">
                      {h}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.raw} className="border-b border-blue-gray-50">
                  <td className="max-w-[260px] py-1.5 pr-4">
                    <Typography variant="small" color="blue-gray" className="truncate font-mono text-[12px]" title={r.raw}>
                      {r.raw}
                    </Typography>
                  </td>
                  <td className="py-1.5 pr-4">
                    <Typography variant="small" color="blue-gray">{r.canonical}</Typography>
                  </td>
                  <td className="py-1.5 pr-4">
                    <Chip
                      size="sm"
                      variant="ghost"
                      color={REGION_CHIP_COLORS[r.region]}
                      value={REGIONS.find((x) => x.key === r.region)?.label || r.region}
                      className="inline-block"
                    />
                  </td>
                  <td className="py-1.5 pr-4">
                    <Typography variant="small" color="gray">{r.count}</Typography>
                  </td>
                  {editable && (
                    <td className="py-1.5 pr-2">
                      <select
                        className="w-40 rounded border border-blue-gray-100 bg-white px-1 py-0.5 text-[12px] text-blue-gray-700"
                        value={overrides?.get(r.raw) || ""}
                        onChange={(e) => setOverride(r.raw, e.target.value || null)}
                      >
                        <option value="">(auto)</option>
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// AI reg extraction: scan photos of unknown-reg listings for the painted
// registration, with metered cost shown before and after.
function RegVisionCard({ market }) {
  const [statsSnap] = useDocument(doc(db, "config", "reg_vision_stats"));
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const stats = statsSnap?.data();
  const unknownCount = market.all.filter((a) => !a.reg).length;

  const run = async () => {
    setRunning(true);
    setRunResult(null);
    const res = await extractUnknownRegs({});
    setRunResult(res);
    setRunning(false);
  };

  return (
    <Card>
      <CardHeader floated={false} shadow={false} color="transparent" className="m-0 flex flex-wrap items-start justify-between gap-3 p-6 pb-0">
        <div>
          <Typography variant="h6" color="blue-gray">
            AI Registration Extraction
          </Typography>
          <Typography variant="small" color="gray" className="max-w-2xl">
            {unknownCount} on-market aircraft have no usable registration. Scanning reads the
            registration off the airframe in the listing photos (gpt-4o-mini vision), checking
            photos one at a time and stopping at the first confident match. Already-scanned
            listings are skipped.
          </Typography>
        </div>
        <Button size="sm" color="blue-gray" disabled={running || unknownCount === 0} onClick={run}>
          {running ? "Scanning photos…" : "Scan unknown registrations"}
        </Button>
      </CardHeader>
      <CardBody className="p-6 pt-3">
        {stats && (
          <Typography variant="small" color="gray">
            All time: {stats.regsFound || 0} registrations recovered from {stats.listingsProcessed || 0} listings
            ({stats.photosScanned || 0} photos scanned) · total cost ${Number(stats.totalCostUsd || 0).toFixed(2)}
            {stats.model ? ` · ${stats.model}` : ""}
          </Typography>
        )}
        {!stats && (
          <Typography variant="small" color="gray">
            Never run. Estimated cost for a full pass: well under $1 (a few thousand vision
            tokens per photo).
          </Typography>
        )}
        {runResult && runResult.success && (
          <Typography variant="small" color="blue-gray" className="mt-2">
            Run complete: {runResult.data.found} found, {runResult.data.notFound} not readable,
            {runResult.data.conflicts ? ` ${runResult.data.conflicts} conflicting (left unknown),` : ""}
            {" "}{runResult.data.noPhotos} without photos · {runResult.data.photosScanned} photos ·
            cost ${Number(runResult.data.costUsd || 0).toFixed(3)}
            {runResult.data.finds?.length
              ? ` · ${runResult.data.finds.map((f) => f.reg).join(", ")}`
              : ""}
          </Typography>
        )}
        {runResult && !runResult.success && (
          <Typography variant="small" color="red" className="mt-2">
            Scan failed: {runResult.error}
          </Typography>
        )}
      </CardBody>
    </Card>
  );
}

// Read-only audit of the geography rules: every raw value currently observed
// on-market and where it lands, for each of the three bases.
function GeoMappingPanel({ market, overrides }) {
  const tables = useMemo(() => {
    const build = (getRaw, getCanon) => {
      const m = new Map();
      market.all.forEach((a) => {
        const raw = getRaw(a) || "(empty)";
        const e = m.get(raw) || { raw, canonical: getCanon(a), count: 0 };
        e.count++;
        m.set(raw, e);
      });
      return [...m.values()]
        .map((e) => ({ ...e, region: regionOf(e.canonical) }))
        .sort((a, b) => b.count - a.count);
    };
    return {
      country: build((a) => a.rawGeo.country, (a) => a.geo.country),
      reg: build((a) => (a.reg ? `${regPrefixOf(a.reg) || "?"}…` : "(no registration)"), (a) => a.geo.reg),
      dealer: build((a) => a.rawGeo.dealerLoc, (a) => a.geo.dealer),
    };
  }, [market]);

  return (
    <div className="space-y-6">
      <Typography variant="small" color="gray" className="max-w-3xl">
        How each observed value maps to a country and region under the three geography bases.
        Regions: United States, Canada, Europe ({EUROPEAN_COUNTRIES.size} countries incl. UK,
        Switzerland and Turkey), Middle East, Asia-Pacific, Latin America, Africa, Other
        (mapped country outside those lists), Unknown (blank or unparseable). The rules are
        code-defined: aliases normalize spellings, ICAO prefixes resolve registrations, and
        free-text locations are parsed for US states first, then country names.
      </Typography>
      <RegVisionCard market={market} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <MappingTable
          title="Country field"
          sub="Raw country value on the listing, normalized. Overrides are saved for everyone."
          rawHeader="Raw value"
          rows={tables.country}
          editable
          overrides={overrides}
        />
        <MappingTable
          title="Registration prefix"
          sub="ICAO registry prefix of the registration (fixed mapping)"
          rawHeader="Prefix"
          rows={tables.reg}
        />
      </div>
      <MappingTable
        title="Dealer location"
        sub="Free-text seller address, parsed (US state signals first, then country names). Overrides are saved for everyone."
        rawHeader="Raw location"
        rows={tables.dealer}
        editable
        overrides={overrides}
      />
    </div>
  );
}

// Donut of the whole market by region under the selected geography basis.
// Slices act like the region tiles: click to focus, click again to clear.
function RegionPie({ regionStats, region, onSelectRegion }) {
  const total = regionStats.reduce((s, r) => s + r.total, 0);
  const CX = 100, CY = 100, R = 72, W = 34;

  const polarPt = (deg, r) => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
  };
  const slicePath = (startDeg, endDeg) => {
    const [sx, sy] = polarPt(startDeg, R);
    const [ex, ey] = polarPt(endDeg, R);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${R} ${R} 0 ${large} 1 ${ex} ${ey}`;
  };

  let cursor = 0;
  const slices = regionStats.map((r) => {
    const sweep = (360 * r.total) / total;
    const gap = regionStats.length > 1 ? 1 : 0;
    const d = slicePath(cursor + gap, Math.min(cursor + sweep - gap, cursor + sweep - 0.01));
    cursor += sweep;
    return { ...r, d };
  });

  return (
    <Card>
      <CardBody className="flex h-full flex-col items-center justify-center p-6">
        <div className="relative w-full max-w-[210px]">
          <svg viewBox="0 0 200 200" className="w-full">
            {slices.map((s) => (
              <path
                key={s.key}
                d={s.d}
                fill="none"
                stroke={REGION_COLORS[s.key]}
                strokeWidth={region === s.key ? W + 6 : W}
                opacity={region === "all" || region === s.key ? 1 : 0.35}
                className="cursor-pointer transition-all duration-300"
                onClick={() => onSelectRegion(s.key)}
              >
                <title>{`${s.label}: ${s.total} aircraft (${pct(s.total, total)})`}</title>
              </path>
            ))}
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            <Typography variant="h4" color="blue-gray">
              {region === "all" ? total : regionStats.find((r) => r.key === region)?.total ?? total}
            </Typography>
            <Typography variant="small" className="text-[10px] uppercase tracking-wide text-blue-gray-400">
              {region === "all" ? "on market" : REGIONS.find((r) => r.key === region)?.label}
            </Typography>
          </div>
        </div>
        <div className="mt-4 grid w-full grid-cols-2 gap-x-5 gap-y-0.5">
          {slices.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`grid grid-cols-[minmax(0,1fr)_2.75rem_3.25rem] items-center rounded px-1 py-0.5 tabular-nums transition-colors hover:bg-blue-gray-50 ${
                region !== "all" && region !== s.key ? "opacity-40" : ""
              }`}
              onClick={() => onSelectRegion(s.key)}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <span
                  className="h-2 w-2 flex-none rounded-full"
                  style={{ backgroundColor: REGION_COLORS[s.key] }}
                />
                <Typography variant="small" color="blue-gray" className="truncate text-[11px]">
                  {s.label}
                </Typography>
              </span>
              <Typography variant="small" color="blue-gray" className="text-right text-[11px] font-medium">
                {s.total}
              </Typography>
              <Typography variant="small" color="blue-gray" className="text-right text-[11px]">
                {pct(s.total, total)}
              </Typography>
            </button>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function MiniGauge({ segments, centerTop, centerSub }) {
  return (
    <div className="relative mx-auto w-full max-w-[170px]">
      <svg viewBox="0 0 300 210" className="w-full">
        <path
          d={arcPath(GAUGE.start, GAUGE.start + GAUGE.sweep)}
          fill="none"
          stroke="#eceff1"
          strokeWidth="20"
          strokeLinecap="round"
        />
        {segments.map((s) => (
          <path key={s.key} d={s.d} fill="none" stroke={s.color} strokeWidth="20" />
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-6 text-center">
        <Typography variant="h4" color="blue-gray">
          {centerTop}
        </Typography>
        <Typography variant="small" color="gray" className="text-[11px]">
          {centerSub}
        </Typography>
      </div>
    </div>
  );
}

// One region column of the comparison strip: same gauge geometry and channel
// order as everywhere else, and channel bars share one scale ACROSS all
// tiles so absolute volumes compare, not just percentages.
function RegionTile({ stat, groupDefs, enabled, maxChannelCount, active, onClick }) {
  const networkCount = groupDefs.reduce(
    (sum, g) => sum + (enabled[g.key] ? stat.counts[g.key] : 0),
    0
  );
  return (
    <div
      className={`cursor-pointer rounded-lg border p-4 transition-colors hover:border-blue-gray-200 ${
        active ? "border-blue-gray-300 bg-blue-gray-50/40 ring-1 ring-blue-gray-200" : "border-blue-gray-50"
      }`}
      onClick={onClick}
    >
      <div className="flex items-baseline justify-between">
        <Typography variant="small" className="font-medium uppercase text-[11px] tracking-wide text-blue-gray-500">
          {stat.label}
        </Typography>
        <Typography variant="small" color="gray">
          {stat.total} on market
        </Typography>
      </div>
      <div className="mt-3">
        <MiniGauge
          segments={buildSegments(groupDefs, stat.counts, stat.total, enabled)}
          centerTop={pct(networkCount, stat.total)}
          centerSub={`${networkCount} network`}
        />
      </div>
      <div className="mt-2 space-y-1.5">
        {groupDefs.map((g) => {
          const count = stat.counts[g.key];
          const on = enabled[g.key];
          return (
            <div key={g.key} className={`flex items-center gap-2 ${on ? "" : "opacity-40"}`}>
              <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: g.color }} />
              <Typography variant="small" color="blue-gray" className="w-16 flex-none text-[11px]">
                {g.short}
              </Typography>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-gray-50">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ backgroundColor: g.color, width: `${(100 * count) / maxChannelCount}%` }}
                />
              </div>
              <Typography variant="small" color="blue-gray" className="w-8 flex-none text-right text-[11px] font-medium">
                {count}
              </Typography>
            </div>
          );
        })}
        {(() => {
          const nn = stat.total - groupDefs.reduce((s, g) => s + stat.counts[g.key], 0);
          return (
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: NON_NETWORK_COLOR }} />
              <Typography variant="small" color="blue-gray" className="w-16 flex-none text-[11px]">
                Non-net
              </Typography>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-gray-50">
                <div
                  className="h-full rounded-full"
                  style={{ backgroundColor: NON_NETWORK_COLOR, width: `${Math.min(100, (100 * nn) / maxChannelCount)}%` }}
                />
              </div>
              <Typography variant="small" color="blue-gray" className="w-8 flex-none text-right text-[11px] font-medium">
                {nn}
              </Typography>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export function CirrusNetworkShareCard() {
  const [enabled, setEnabled] = useState({
    lone_mountain: true,
    aerista: true,
    cirrus_direct: true,
    off_feed: true,
  });
  const [drill, setDrill] = useState(null); // group key | "network" | "market" | null

  const listingsQuery = useMemo(
    () =>
      query(
        collection(db, "listing_aggregations_v1"),
        and(
          where("manufacturer", "in", ["cirrus", "cirrus aircraft"]),
          where("last_seen", ">=", periodToDate(PERIOD))
        )
      ),
    []
  );

  const [snap, loading] = useCollection(listingsQuery);
  const [configSnap] = useDocument(doc(db, "config", "cirrus_configurator"));
  const [overridesSnap] = useCollection(query(collection(db, "geo_mapping_overrides")));

  const fleetSize = configSnap?.data()?.rowCount || null;

  // User-edited mapping config (stored in Firestore): raw value -> country.
  const overrides = useMemo(() => {
    const m = new Map();
    overridesSnap?.docs.forEach((d) => {
      const x = d.data();
      if (x.raw && x.country) m.set(x.raw.trim(), x.country);
    });
    return m;
  }, [overridesSnap]);

  const market = useMemo(() => {
    if (!snap) return null;

    // Feed membership must be CURRENT: an aircraft that left the cirrus feed
    // still has a doc inside the 7d market window for days afterwards. An
    // aircraft is "on the feed" only if it belongs to the newest scrape run
    // (grouped by scrapeId — each doc carries the id of the last run that
    // touched it). A run in progress is detected as: recent writes AND the
    // newest run still covers well under the full feed (docs flip from the
    // previous run's id to the new one as the run progresses). While in
    // progress, the previous run also counts, so aircraft the run has not
    // reached yet do not flicker out mid-scrape; once ~90% have flipped the
    // run is treated as complete and only the newest run counts.
    const SCRAPE_ACTIVE_MS = 2 * 3600 * 1000;
    const runs = new Map(); // scrapeId -> {maxSeen, count}
    let totalFeedDocs = 0;
    snap.docs.forEach((d) => {
      const data = d.data();
      if (data.source === "cirrus" && data.scrapeId) {
        totalFeedDocs++;
        const t = data.last_seen?.toDate?.()?.getTime() || 0;
        const r = runs.get(data.scrapeId) || { maxSeen: 0, count: 0 };
        r.maxSeen = Math.max(r.maxSeen, t);
        r.count++;
        runs.set(data.scrapeId, r);
      }
    });
    const runOrder = [...runs.entries()].sort((a, b) => b[1].maxSeen - a[1].maxSeen);
    const feedUpdating =
      runOrder.length > 1 &&
      Date.now() - runOrder[0][1].maxSeen < SCRAPE_ACTIVE_MS &&
      runOrder[0][1].count < 0.9 * totalFeedDocs;
    const activeScrapeIds = new Set();
    if (runOrder[0]) activeScrapeIds.add(runOrder[0][0]);
    if (feedUpdating && runOrder[1]) activeScrapeIds.add(runOrder[1][0]);

    // Merge the per-source aggregation docs into one record per aircraft.
    // The cirrus-feed doc wins for display fields (canonical data); otherwise
    // the most recently seen doc does.
    const byReg = new Map();
    snap.docs.forEach((d) => {
      const data = d.data();
      const key = regKey(data);
      const lastSeen = data.last_seen?.toDate?.()?.getTime() || 0;
      if (!byReg.has(key)) {
        byReg.set(key, { key, feedDealers: [], otherDealers: [], sources: [], best: null, bestIsFeed: false, lastSeen: 0 });
      }
      const rec = byReg.get(key);
      const isFeed = data.source === "cirrus" && activeScrapeIds.has(data.scrapeId);
      if (data.dealerName && data.dealerName !== "NO_DEALER_NAME") {
        if (isFeed) rec.feedDealers.push(data.dealerName);
        else if (data.source !== "cirrus") rec.otherDealers.push(data.dealerName);
        // stale cirrus docs classify nothing: the aircraft has left the feed
      }
      if (!rec.sources.some((s) => s.source === data.source)) {
        rec.sources.push({ source: data.source, url: data.listingURL || null, lastSeen });
      }
      rec.lastSeen = Math.max(rec.lastSeen, lastSeen);
      if (!rec.best || (isFeed && !rec.bestIsFeed) || (isFeed === rec.bestIsFeed && lastSeen > (rec.best._ls || 0))) {
        rec.best = { ...data, _ls: lastSeen };
        rec.bestIsFeed = isFeed;
      }
    });

    const groups = { lone_mountain: [], aerista: [], cirrus_direct: [], off_feed: [] };
    const all = [];
    byReg.forEach((rec) => {
      const b = rec.best || {};
      const sources = [...rec.sources].sort((a2, b2) =>
        a2.source === "cirrus" ? -1 : b2.source === "cirrus" ? 1 : b2.lastSeen - a2.lastSeen
      );
      const freshCirrus = rec.feedDealers.length > 0 ? sources.find((s) => s.source === "cirrus" && s.url) : null;
      const regInfo = displayReg(b);
      const ov = (raw) => overrides.get((raw || "").trim()) || null;
      const aircraft = {
        key: rec.key,
        listingId: b.uniqueListingId,
        reg: regInfo.reg,
        regSource: regInfo.source,
        regConfidence: regInfo.confidence,
        geo: {
          country: ov(b.country) || canonicalCountry(b.country),
          reg: countryFromReg(regInfo.reg),
          dealer: ov(b.dealerLocation) || countryFromDealerLocation(b.dealerLocation),
        },
        rawGeo: { country: b.country || "", dealerLoc: b.dealerLocation || "" },
        year: b.year,
        model: b.formattedModel || b.model || "",
        price: b.price,
        priceUsd: b.price_usd,
        currency: b.currency,
        dealer: rec.feedDealers[0] || b.dealerName || rec.otherDealers[0] || "",
        sources,
        primaryUrl:
          freshCirrus?.url ||
          [...sources].sort((a2, b2) => b2.lastSeen - a2.lastSeen).find((s) => s.url)?.url ||
          null,
        lastSeen: rec.lastSeen,
      };
      all.push(aircraft);
      if (rec.feedDealers.length > 0) {
        aircraft.group = partnerForCirrusFeed(rec.feedDealers);
        groups[aircraft.group].push(aircraft);
      } else if (partnerFor(rec.otherDealers)) {
        aircraft.group = "off_feed";
        groups.off_feed.push(aircraft);
      } else {
        aircraft.group = "non_network";
      }
    });

    return { total: byReg.size, groups, all, feedUpdating };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap, overrides]);

  const groupDefs = [...PARTNERS, OFF_FEED];

  const [geoBasis, setGeoBasis] = useState("country");
  const [region, setRegion] = useState("all");
  const [mappingOpen, setMappingOpen] = useState(false);

  // Region-filtered view of the market under the selected geography basis.
  // Also computes the whole-market region distribution for the current basis
  // so the impact of switching basis is directly visible.
  const view = useMemo(() => {
    if (!market) return null;
    const inRegion = (a) => region === "all" || regionOf(a.geo[geoBasis]) === region;
    const groups = {};
    groupDefs.forEach((g) => {
      groups[g.key] = market.groups[g.key].filter(inRegion);
    });
    const all = market.all.filter(inRegion);
    return { total: all.length, groups, all, nonNetwork: all.filter((a) => a.group === "non_network") };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, geoBasis, region]);

  // Fleet denominator follows the region focus. The per-region split is
  // precomputed from the manifest's registrations (prefix -> region) and
  // stored on the config doc as regionCounts, so region fleet percentages
  // are always registry-based regardless of the selected geography basis.
  const regionCounts = configSnap?.data()?.regionCounts || null;
  const fleetSizeForView = region === "all" ? fleetSize : regionCounts?.[region] || null;

  // Per-region stats for the comparison strip (whole market, not region-
  // filtered — the strip IS the region breakdown under the current basis).
  const regionStats = useMemo(() => {
    if (!market) return null;
    return REGIONS.filter((r) => r.key !== "all")
      .map((r) => {
        const inR = (a) => regionOf(a.geo[geoBasis]) === r.key;
        const counts = {};
        groupDefs.forEach((g) => {
          counts[g.key] = market.groups[g.key].filter(inR).length;
        });
        return { ...r, counts, total: market.all.filter(inR).length };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, geoBasis]);

  const maxChannelCount = regionStats
    ? Math.max(1, ...regionStats.flatMap((r) => groupDefs.map((g) => r.counts[g.key])))
    : 1;

  const networkAircraft = view
    ? groupDefs.flatMap((g) => (enabled[g.key] ? view.groups[g.key] : []))
    : [];
  const networkCount = networkAircraft.length;

  // hero gauge segments for the current (possibly region-filtered) view
  const segments = useMemo(() => {
    if (!view) return [];
    const counts = {};
    groupDefs.forEach((g) => {
      counts[g.key] = view.groups[g.key].length;
    });
    return buildSegments(groupDefs, counts, view.total, enabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, enabled]);

  const maxGroupCount = view
    ? Math.max(1, ...groupDefs.map((g) => view.groups[g.key].length))
    : 1;

  // Top non-network sellers in the current view, with a rollup for the tail.
  const nonNetworkSellers = useMemo(() => {
    if (!view) return [];
    const byDealer = new Map();
    view.nonNetwork.forEach((a) => {
      const raw = (a.dealer || "").trim();
      const name = !raw || raw === "NO_DEALER_NAME" ? "Private / unlisted seller" : raw;
      if (!byDealer.has(name)) byDealer.set(name, []);
      byDealer.get(name).push(a);
    });
    const sorted = [...byDealer.entries()]
      .map(([name, aircraft]) => ({ name, aircraft, count: aircraft.length }))
      .sort((a, b) => b.count - a.count);
    const top = sorted.slice(0, 8);
    const tail = sorted.slice(8);
    if (tail.length) {
      top.push({
        name: `Other sellers (${tail.length})`,
        aircraft: tail.flatMap((t) => t.aircraft),
        count: tail.reduce((s, t) => s + t.count, 0),
        isRollup: true,
      });
    }
    return top;
  }, [view]);

  let drillData = null;
  if (view && drill) {
    if (drill === "non_network") {
      drillData = { group: { label: "Non-network aircraft", color: NON_NETWORK_COLOR }, aircraft: view.nonNetwork };
    } else if (drill.startsWith("nn:")) {
      const seller = nonNetworkSellers.find((s) => s.name === drill.slice(3));
      drillData = seller ? { group: { label: seller.name, color: NON_NETWORK_COLOR }, aircraft: seller.aircraft } : null;
    } else {
      const g = groupDefs.find((g2) => g2.key === drill);
      drillData = g ? { group: g, aircraft: view.groups[drill] } : null;
    }
  }

  const toggleDrill = (key) => setDrill((d) => (d === key ? null : key));

  // The drill panel renders below the channel cards — scroll it into view
  // when opened so clicking a stat tile visibly does something.
  const drillRef = useRef(null);
  useEffect(() => {
    if (drill && drillRef.current) {
      drillRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [drill]);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <div className="flex items-center gap-2">
          <Typography variant="h5" color="blue-gray">
            Cirrus Network Share
          </Typography>
          <Tooltip
            className="max-w-xs"
            content="Aircraft in the cirrusaircraft.com pre-owned feed, as a share of every Cirrus on the market, deduped by registration across Controller, Trade-A-Plane, Planecheck, Globalair and the Cirrus feed itself."
          >
            <InformationCircleIcon className="h-5 w-5 cursor-help text-blue-gray-300" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          {market?.feedUpdating && (
            <Chip size="sm" variant="ghost" color="amber" value="feed update in progress" />
          )}
          <Chip size="sm" variant="ghost" color="blue-gray" value={`${PERIOD} window`} />
          <IconButton
            variant="text"
            color="blue-gray"
            size="sm"
            title="Country mapping & AI configuration"
            onClick={() => setMappingOpen(true)}
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </IconButton>
        </div>
      </div>

      {loading && (
        <Typography variant="small" color="gray">
          Loading listings…
        </Typography>
      )}

      {view && (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-gray-100 bg-white px-3 py-2">
              <Typography
                variant="small"
                className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-blue-gray-500"
              >
                Geography basis
              </Typography>
              <select
                className="cursor-pointer bg-transparent text-sm font-medium text-blue-gray-800 focus:outline-none"
                value={geoBasis}
                onChange={(e) => setGeoBasis(e.target.value)}
              >
                {GEO_BASES.map((b) => (
                  <option key={b.key} value={b.key}>{b.label}</option>
                ))}
              </select>
            </label>
            <Typography variant="small" color="gray">
              Regions are classified by the selected basis. Click a region tile to focus the
              figures below on it.
            </Typography>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            <Card>
              <CardBody className="flex flex-col items-center p-6">
                <div className="relative w-full max-w-[380px]">
                  <svg viewBox="0 0 300 210" className="w-full">
                    <path
                      d={arcPath(GAUGE.start, GAUGE.start + GAUGE.sweep)}
                      fill="none"
                      stroke="#eceff1"
                      strokeWidth="14"
                      strokeLinecap="round"
                    />
                    {segments.map((s) => (
                      <path key={s.key} d={s.d} fill="none" stroke={s.color} strokeWidth="14" />
                    ))}
                  </svg>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-4 text-center">
                    <Typography variant="h1" color="blue-gray">
                      {pct(networkCount, view.total)}
                    </Typography>
                    <Typography variant="small" className="uppercase text-[11px] tracking-wide text-blue-gray-400">
                      of on-market Cirrus{region !== "all" ? ` (${REGIONS.find((r) => r.key === region).label})` : ""}
                    </Typography>
                    <Typography variant="small" color="gray">
                      {networkCount} of {view.total} aircraft
                    </Typography>
                  </div>
                </div>
              </CardBody>
            </Card>

            {regionStats && regionStats.length > 0 && (
              <RegionPie
                regionStats={regionStats}
                region={region}
                onSelectRegion={(key) => setRegion((r) => (r === key ? "all" : key))}
              />
            )}

            <Card className="overflow-hidden">
              <div className="grid h-full grid-cols-2 gap-px bg-blue-gray-50">
                <StatTile
                  label="Network aircraft"
                  value={networkCount}
                  foot="across enabled channels"
                />
                <StatTile
                  label="On-market Cirrus"
                  value={view.total}
                  foot={`unique registrations · ${PERIOD}`}
                />
                <StatTile
                  label="Network / fleet"
                  value={fleetSizeForView ? pct(networkCount, fleetSizeForView) : "n/a"}
                  foot={
                    fleetSizeForView
                      ? region !== "all"
                        ? `fleet ${fleetSizeForView.toLocaleString()} in region (by reg prefix)`
                        : `fleet ${fleetSizeForView.toLocaleString()} (configurator)`
                      : "manifest unavailable"
                  }
                />
                <StatTile
                  label="Market / fleet"
                  value={fleetSizeForView ? pct(view.total, fleetSizeForView) : "n/a"}
                  foot="all on-market Cirrus"
                />
              </div>
            </Card>
          </div>

          {regionStats && regionStats.length > 1 && (
            <Card className="mb-6">
              <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography variant="h6" color="blue-gray">
                      Regional Breakdown
                    </Typography>
                    <Typography variant="small" color="gray">
                      Network share of each region's on-market Cirrus, by{" "}
                      {GEO_BASES.find((b) => b.key === geoBasis).label.toLowerCase()}. Channel bars
                      share one scale across regions.
                    </Typography>
                  </div>
                  {region !== "all" && (
                    <Chip
                      size="sm"
                      variant="ghost"
                      color="blue-gray"
                      value={`focused: ${REGIONS.find((r) => r.key === region).label}`}
                      onClose={() => setRegion("all")}
                    />
                  )}
                </div>
              </CardHeader>
              <CardBody className="p-6 pt-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {regionStats.map((stat) => (
                    <RegionTile
                      key={stat.key}
                      stat={stat}
                      groupDefs={groupDefs}
                      enabled={enabled}
                      maxChannelCount={maxChannelCount}
                      active={region === stat.key}
                      onClick={() => setRegion((r) => (r === stat.key ? "all" : stat.key))}
                    />
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
              <Typography variant="h6" color="blue-gray">
                Network Channels
                {region !== "all" ? ` (${REGIONS.find((r) => r.key === region).label})` : ""}
              </Typography>
              <Typography variant="small" color="gray">
                Toggle a channel to include or exclude it from the share above. Click a row to
                see its aircraft.
              </Typography>
            </CardHeader>
            <CardBody className="p-6 pt-4">
              <ChannelGridHeader nameLabel="Channel" />
              <div className="divide-y divide-blue-gray-50">
                {groupDefs.map((g) => {
                  const aircraft = view.groups[g.key];
                  const count = aircraft.length;
                  const on = enabled[g.key];
                  const isDrilled = drill === g.key;
                  return (
                    <div
                      key={g.key}
                      className={`${CHANNEL_GRID} cursor-pointer rounded-md px-2 py-3 transition-colors hover:bg-blue-gray-50/50 ${
                        on ? "" : "opacity-50"
                      } ${isDrilled ? "bg-blue-gray-50/50" : ""}`}
                      onClick={() => toggleDrill(g.key)}
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={on}
                          onChange={() => setEnabled((e) => ({ ...e, [g.key]: !e[g.key] }))}
                          containerProps={{ className: "p-1" }}
                        />
                      </span>
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: g.color }}
                      />
                      <div className="min-w-0">
                        <Typography variant="small" color="blue-gray" className="truncate font-medium" title={g.label}>
                          {g.label}
                        </Typography>
                        <Typography variant="small" color="gray" className="truncate" title={g.sub || undefined}>
                          {count} aircraft{g.sub ? ` · ${g.sub}` : ""}
                        </Typography>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-blue-gray-50">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: g.color,
                            width: on ? `${(100 * count) / maxGroupCount}%` : "0%",
                          }}
                        />
                      </div>
                      <Typography variant="small" color="blue-gray" className="text-right font-medium">
                        {pct(count, view.total)}
                      </Typography>
                      <Typography variant="small" color="blue-gray" className="text-right font-medium">
                        {fleetSizeForView ? pct(count, fleetSizeForView) : "n/a"}
                      </Typography>
                      <ChevronDownIcon
                        className={`h-4 w-4 justify-self-end text-blue-gray-300 transition-transform ${
                          isDrilled ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          <Card className="mt-6">
            <CardHeader floated={false} shadow={false} color="transparent" className="m-0 p-6 pb-0">
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={() => toggleDrill("non_network")}
              >
                <div>
                  <Typography variant="h6" color="blue-gray">
                    Non-Network Channels
                    {region !== "all" ? ` (${REGIONS.find((r) => r.key === region).label})` : ""}
                  </Typography>
                  <Typography variant="small" color="gray">
                    {view.nonNetwork.length} aircraft ({pct(view.nonNetwork.length, view.total)} of market) listed
                    outside the network — the grey gauge segment. Click a seller to see their aircraft.
                  </Typography>
                </div>
                <ChevronDownIcon
                  className={`h-4 w-4 text-blue-gray-300 transition-transform ${
                    drill === "non_network" ? "rotate-180" : ""
                  }`}
                />
              </div>
            </CardHeader>
            <CardBody className="p-6 pt-4">
              <ChannelGridHeader nameLabel="Seller" />
              <div className="divide-y divide-blue-gray-50">
                {nonNetworkSellers.map((s) => {
                  const drillKey = `nn:${s.name}`;
                  const isDrilled = drill === drillKey;
                  const maxSeller = nonNetworkSellers[0]?.count || 1;
                  return (
                    <div
                      key={s.name}
                      className={`${CHANNEL_GRID} cursor-pointer rounded-md px-2 py-2.5 transition-colors hover:bg-blue-gray-50/50 ${
                        isDrilled ? "bg-blue-gray-50/50" : ""
                      }`}
                      onClick={() => toggleDrill(drillKey)}
                    >
                      <span
                        className="h-3 w-3 justify-self-center rounded-full"
                        style={{ backgroundColor: NON_NETWORK_COLOR }}
                      />
                      <span />
                      <div className="min-w-0">
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className={`truncate ${s.isRollup ? "" : "font-medium"}`}
                          title={s.name}
                        >
                          {s.name}
                        </Typography>
                        <Typography variant="small" color="gray">
                          {s.count} aircraft
                        </Typography>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-blue-gray-50">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: NON_NETWORK_COLOR,
                            width: `${(100 * s.count) / maxSeller}%`,
                          }}
                        />
                      </div>
                      <Typography variant="small" color="blue-gray" className="text-right font-medium">
                        {pct(s.count, view.total)}
                      </Typography>
                      <Typography variant="small" color="blue-gray" className="text-right font-medium">
                        {fleetSizeForView ? pct(s.count, fleetSizeForView) : "n/a"}
                      </Typography>
                      <ChevronDownIcon
                        className={`h-4 w-4 justify-self-end text-blue-gray-300 transition-transform ${
                          isDrilled ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          {drillData && drillData.group && (
            <div ref={drillRef} className="scroll-mt-4">
            <DrillDown
              key={`${drill}|${region}|${geoBasis}`}
              group={drillData.group}
              aircraft={drillData.aircraft}
              onClose={() => setDrill(null)}
              fleetSize={fleetSizeForView}
              marketTotal={view.total}
              geoBasis={geoBasis}
            />
            </div>
          )}
        </>
      )}

      {market && (
        <Dialog open={mappingOpen} handler={() => setMappingOpen(false)} size="xl">
          <DialogHeader className="flex items-center justify-between">
            <div>
              <Typography variant="h5" color="blue-gray">
                Country Mapping & AI Configuration
              </Typography>
              <Typography variant="small" color="gray" className="font-normal">
                How listings are mapped to countries and regions, with per-value overrides.
              </Typography>
            </div>
            <IconButton variant="text" color="blue-gray" onClick={() => setMappingOpen(false)}>
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </DialogHeader>
          <DialogBody className="max-h-[75vh] overflow-y-auto bg-blue-gray-50/40">
            <GeoMappingPanel market={market} overrides={overrides} />
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
}

export default CirrusNetworkShareCard;
